//-- Copyright (c) 2021 TMT Insights Group, LLC
import styled from '@emotion/styled';
import { Tooltip, lighten } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';

// taken from https://codesandbox.io/embed/react-animated-progress-bar-rh3rz?fontsize=14
// implementation on component below.
type VisualPart = {
  percentage: string;
  color: string;
};
type ProgressLineProps = {
  label?: string;
  backgroundColor?: string;
  visualParts?: VisualPart[];
};
const ProgressLine = ({
  label,
  backgroundColor = '#e5e5e5',
  // expected format for visual parts
  visualParts = [{ percentage: '0%', color: 'white' }]
}: ProgressLineProps) => {
  // Starting values needed for the animation
  // Mapped by "visualParts" so it can work with multiple values dynamically
  // It's an array of percentage widths
  const [widths, setWidths] = useState<string[]>(new Array(visualParts.length).fill('0%'));

  useEffect(() => {
    // https://developer.mozilla.org/en-US/docs/Web/API/window/requestAnimationFrame
    // You need to wrap it to trigger the animation
    requestAnimationFrame(() => {
      // Set a new array of percentage widths based on the props
      setWidths(visualParts.map((item) => item.percentage));
    });
  }, [visualParts]);

  return (
    <>
      {label && <div>{label}</div>}
      <ProgressVisualFull style={{ backgroundColor }}>
        {visualParts.map((item, index) => {
          // map each part into separate div and each will be animated
          // because of the "transition: width 2s;" css in class "progressVisualPart"
          // and because of the new width ("widths[index]", previous one was 0)
          return (
            <ProgressVisualPart
              key={`${item.color}_${index}`}
              style={{
                width: widths[index],
                backgroundColor: item.color
              }}
            />
          );
        })}
      </ProgressVisualFull>
    </>
  );
};

const ProgressVisualFull = styled.div`
  display: flex;
  height: 8px;
  margin: 20px 0;
  border-radius: 20px;
  overflow: hidden;
`;

const ProgressVisualPart = styled.div`
  transition: width 2s;
`;

type LinearPercentageProps = {
  total?: number;
  pending?: number;
  complete?: number;
  error?: number;
  cancelled?: number;
  tooltipText?: string;
};
const LinearPercentage = ({ total = 0, pending = 0, complete = 0, error = 0, cancelled = 0, tooltipText }: LinearPercentageProps) => {
  const theme = useTheme();
  const pendingPercentage = (pending / total) * 100;
  const errorPercentage = (error / total) * 100;
  const completedPercentage = !total ? 100 : (complete / total) * 100;
  const cancelledPercentage = (cancelled / total) * 100;
  const totalPercentage = 100 - pendingPercentage - completedPercentage - errorPercentage - cancelledPercentage;

  const renderTooltip = () => <Tooltip title={tooltipText}>{renderProgressLine()}</Tooltip>;

  const renderProgressLine = () => (
    <div style={{ width: '100%' }}>
      <ProgressLine
        visualParts={
          !total
            ? [
                {
                  percentage: `${completedPercentage}%`,
                  color: lighten(theme.palette.background.paper, 0.2)
                }
              ]
            : [
                {
                  percentage: `${completedPercentage}%`,
                  color: theme.palette.success.main
                },
                {
                  percentage: `${pendingPercentage}%`,
                  color: theme.palette.warning.main
                },
                {
                  percentage: `${errorPercentage}%`,
                  color: theme.palette.error.main
                },
                {
                  percentage: `${cancelledPercentage}%`,
                  color: theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300]
                },
                {
                  percentage: `${totalPercentage}%`,
                  color: theme.palette.default.main
                }
              ]
        }
      />
    </div>
  );

  if (tooltipText) return renderTooltip();
  return renderProgressLine();
};

export default LinearPercentage;
